/**
 * Tracking de FOTOS (Cloudinary) — impresiones y aperturas por public_id.
 *
 * "Quién vio cada foto": cada evento viaja con el distinct_id de PostHog y
 * las super-props de geo del Worker (worker_country/province/city) — el admin
 * las cruza en /admin/fotos-analytics vía /api/admin/photo-analytics.
 *
 * Eventos:
 *   photo_impression    — la foto estuvo ≥50% visible por ≥800ms (1 vez por
 *                         foto por pageload).
 *   photo_open          — la foto se abrió en lightbox / vista completa.
 *
 * La clave de join con el catálogo de Cloudinary es photo_public_id
 * (extraído de la URL res.cloudinary.com). Fotos no-Cloudinary usan la URL
 * como id de mejor esfuerzo.
 */

import posthog from "posthog-js";
import { isPostHogConfigured } from "@/lib/posthogAnalytics";

/** res.cloudinary.com/<cloud>/image/upload/<transforms>/v123/<public_id>.<ext> → public_id */
export function cloudinaryPublicIdFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://x.invalid");
    if (!u.hostname.includes("res.cloudinary.com")) return null;
    const parts = u.pathname.split("/").filter(Boolean);
    const uploadIdx = parts.indexOf("upload");
    if (uploadIdx < 0) return null;
    let rest = parts.slice(uploadIdx + 1);
    // Saltar segmentos de transformación (contienen "_" con clave=valor tipo
    // w_400,q_auto) y el segmento de versión (v123456...).
    while (rest.length > 1 && /^([a-z]+_[^/]+)(,|$)/.test(rest[0])) rest = rest.slice(1);
    if (rest.length > 1 && /^v\d+$/.test(rest[0])) rest = rest.slice(1);
    if (rest.length === 0) return null;
    const joined = rest.join("/");
    return joined.replace(/\.[a-zA-Z0-9]+$/, "");
  } catch {
    return null;
  }
}

export interface PhotoTrackContext {
  section: string;
  templateType?: string;
  countryCode?: string;
}

function basePhotoProps(src: string, position: number | undefined, ctx: PhotoTrackContext) {
  const publicId = cloudinaryPublicIdFromUrl(src);
  return {
    photo_public_id: publicId ?? src.slice(0, 200),
    photo_is_cloudinary: publicId != null,
    photo_src: src.slice(0, 300),
    photo_position: position,
    photo_section: ctx.section,
    template_type: ctx.templateType,
    country_code: ctx.countryCode,
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
  };
}

export function trackPhotoImpression(src: string, position: number | undefined, ctx: PhotoTrackContext): void {
  if (!isPostHogConfigured()) return;
  posthog.capture("photo_impression", basePhotoProps(src, position, ctx));
}

export function trackPhotoOpen(src: string, position: number | undefined, ctx: PhotoTrackContext): void {
  if (!isPostHogConfigured()) return;
  posthog.capture("photo_open", basePhotoProps(src, position, ctx));
}
